import { z } from "zod";

export const avatarPresetIds = [
  "amber-arch",
  "blue-orbit",
  "coral-kite",
  "green-spark",
  "indigo-drop",
  "lime-cube",
  "pink-moon",
  "red-hex",
  "slate-wave",
  "teal-star",
  "violet-ring",
  "yellow-prism",
] as const;

export const gauntletDefinitionIds = [
  "ember",
  "flint",
  "harbor",
  "kestrel",
  "morrow",
  "orbit",
  "quartz",
  "rook",
  "solace",
  "tangent",
  "vale",
  "wren",
  "xenon",
  "yarrow",
  "zephyr",
  "aurora",
] as const;

export const ProfileSchema = z.object({
  displayName: z.string().trim().min(1).max(40),
  avatarSetVersion: z.literal("shapes-v1"),
  avatarPresetId: z.enum(avatarPresetIds),
});
export type Profile = z.infer<typeof ProfileSchema>;

export const SessionSchema = z.object({
  authenticated: z.boolean(),
  user: z.object({
    id: z.string(),
    emailVerified: z.boolean(),
    profile: ProfileSchema.nullable(),
  }).nullable(),
  loginUrl: z.string(),
});
export type Session = z.infer<typeof SessionSchema>;

export const InstanceStatusSchema = z.enum([
  "draft",
  "inviting",
  "setup",
  "ready",
  "live",
  "completed",
  "archived",
  "cancelled",
]);
export type InstanceStatus = z.infer<typeof InstanceStatusSchema>;

export const InstanceRoleSchema = z.enum(["host", "cohost", "player"]);
export type InstanceRole = z.infer<typeof InstanceRoleSchema>;

export const GameInstanceSchema = z.object({
  id: z.string(),
  productId: z.literal("gauntlet"),
  name: z.string(),
  startsAt: z.string().datetime(),
  timeZone: z.string(),
  status: InstanceStatusSchema,
  role: InstanceRoleSchema,
  version: z.number().int().nonnegative(),
  nextAction: z.string(),
});
export type GameInstance = z.infer<typeof GameInstanceSchema>;

export const CreateInstanceSchema = z.object({
  name: z.string().trim().min(3).max(80),
  startsAt: z.string().datetime(),
  timeZone: z.string().trim().min(1).max(64),
  organizerResponsibilityAccepted: z.literal(true),
});
export type CreateInstance = z.infer<typeof CreateInstanceSchema>;

export const CreateInvitationSchema = z.object({
  recipientEmail: z.string().trim().email().max(254).optional(),
});
export type CreateInvitation = z.infer<typeof CreateInvitationSchema>;

export const InvitationPreviewSchema = z.object({
  invitationId: z.string(),
  instanceId: z.string(),
  instanceName: z.string(),
  startsAt: z.string().datetime(),
  timeZone: z.string(),
  inviterDisplayName: z.string(),
  expiresAt: z.string().datetime(),
  stage: z.literal("external-alpha"),
  materialNotice: z.string(),
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;

export const GameSubmissionInputSchema = z.object({
  title: z.string().trim().min(3).max(80),
  summary: z.string().trim().min(10).max(500),
  instructions: z.string().trim().min(10).max(2000),
  durationMinutes: z.number().int().min(1).max(180),
  location: z.string().trim().min(1).max(120),
  materials: z.string().trim().max(500),
  physicalDemands: z.string().trim().max(500),
  sensoryDemands: z.string().trim().max(500),
  socialDemands: z.string().trim().max(500),
  costOrConsumption: z.string().trim().max(500),
  alternative: z.string().trim().min(3).max(1000),
  scoring: z.string().trim().min(3).max(1000),
});
export type GameSubmissionInput = z.infer<typeof GameSubmissionInputSchema>;

export const GameSubmissionSchema = GameSubmissionInputSchema.extend({
  id: z.string(),
  revision: z.number().int().positive(),
  authorUserId: z.string(),
  status: z.enum(["pending", "approved", "changes_requested"]),
  reviewNote: z.string().optional(),
  createdAt: z.string().datetime(),
});
export type GameSubmission = z.infer<typeof GameSubmissionSchema>;

export const SubmissionReviewSchema = z.object({
  decision: z.enum(["approved", "changes_requested"]),
  note: z.string().trim().max(500).optional(),
});
export type SubmissionReview = z.infer<typeof SubmissionReviewSchema>;

export const GauntletSetupSchema = z.object({
  instance: GameInstanceSchema,
  members: z.array(z.object({
    userId: z.string(),
    displayName: z.string(),
    role: InstanceRoleSchema,
    state: z.enum(["joined", "left", "removed"]),
    profileComplete: z.boolean(),
    submissionStatus: z.enum(["missing", "pending", "approved", "changes_requested"]),
    allocationId: z.enum(gauntletDefinitionIds).nullable(),
  })),
  submissions: z.array(GameSubmissionSchema),
  availableDefinitionIds: z.array(z.enum(gauntletDefinitionIds)),
  readiness: z.object({
    eligible: z.boolean(),
    blockers: z.array(z.string()),
  }),
});
export type GauntletSetup = z.infer<typeof GauntletSetupSchema>;

export const ProblemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  code: z.string(),
  requestId: z.string(),
  errors: z.array(z.object({ path: z.string(), message: z.string() })).default([]),
});
export type Problem = z.infer<typeof ProblemSchema>;
